import { GUID } from "@faro-lotv/foundation";
import { create } from "zustand";

/** 1h timeout until the data has to be fully reloaded again. */
const LOAD_TIMEOUT_MS = 60 * 60 * 1000;

type ProjectLoadingStore = {
  /** The ID of the project being loaded. When the ID changes, everything has to be reloaded. */
  projectId?: GUID;
  /** The ID of the revision being loaded. When the ID changes, everything has to be reloaded. */
  revisionId?: GUID | "latest";

  /** The time when the initial project data has been loaded last. */
  initialDataTime?: Date;
  /** Whether the initial project data should be reloaded. */
  mustLoadInitialData(projectId: GUID): boolean;
  /** Signal that the initial project data has been loaded. */
  initialDataLoaded(projectId: GUID): void;

  /** The time when the project metadata has been loaded last. */
  projectMetadataTime?: Date;
  /** Whether the project metadata should be reloaded. */
  mustLoadProjectMetadata(projectId: GUID): boolean;
  /** Signal that the project meta data has been loaded. */
  projectMetadataLoaded(projectId: GUID): void;

  /** The time when the revision data for the Draft view has been loaded last. */
  revisionDataTime?: Date;
  /** Whether the revision data for the Draft view should be reloaded. */
  mustLoadRevisionData(revisionId: GUID | "latest"): boolean;
  /** Signal that the revision data for the Draft view has been loaded. */
  revisionDataLoaded(revisionId: GUID | "latest"): void;

  /** The time when the root element for the Draft view has been loaded last. */
  rootElementTime?: Date;
  /** Whether the root element for the Draft view should be reloaded. */
  mustLoadRootElement(projectId: GUID): boolean;
  /** Signal that the root element for the Draft view has been loaded. */
  rootElementLoaded(projectId: GUID): void;
};

/**
 * Store the times where the project data has been last loaded, to avoid unnecessary reloads.
 * Also for the data requested by the Draft view.
 */
export const useProjectLoadingStore = create<ProjectLoadingStore>()(
  (set, getState) => ({
    mustLoadInitialData: (projectId) => {
      if (projectId !== getState().projectId) return true;

      const lastLoad = getState().initialDataTime;
      if (!lastLoad) return true;

      const durationMs = new Date().valueOf() - lastLoad.valueOf();
      return durationMs > LOAD_TIMEOUT_MS;
    },
    initialDataLoaded: (projectId) => {
      const time = new Date();
      set({ projectId, initialDataTime: time });
    },

    mustLoadProjectMetadata: (projectId) => {
      if (projectId !== getState().projectId) return true;

      const lastLoad = getState().projectMetadataTime;
      if (!lastLoad) return true;

      const durationMs = new Date().valueOf() - lastLoad.valueOf();
      return durationMs > LOAD_TIMEOUT_MS;
    },
    projectMetadataLoaded: (projectId) => {
      const time = new Date();
      set({ projectId, projectMetadataTime: time });
    },

    mustLoadRevisionData: (revisionId) => {
      if (revisionId !== getState().revisionId) return true;

      const lastLoad = getState().revisionDataTime;
      if (!lastLoad) return true;

      const durationMs = new Date().valueOf() - lastLoad.valueOf();
      return durationMs > LOAD_TIMEOUT_MS;
    },
    revisionDataLoaded: (revisionId) => {
      const time = new Date();
      set({ revisionId, revisionDataTime: time });
    },

    mustLoadRootElement: (projectId) => {
      if (projectId !== getState().projectId) return true;

      const lastLoad = getState().rootElementTime;
      if (!lastLoad) return true;

      const durationMs = new Date().valueOf() - lastLoad.valueOf();
      return durationMs > LOAD_TIMEOUT_MS;
    },
    rootElementLoaded: (projectId) => {
      const time = new Date();
      set({ projectId, rootElementTime: time });
    },
  }),
);
